import { useState, useEffect, forwardRef } from "react";
import moment from "moment";
// Redux
import { useDispatch, useSelector } from "react-redux";
import { addDismissedNotification } from "../../../../redux/recsysDucks";
// Components
import {
  MainDiv,
  Header,
  WarningImg,
  TitlesBox,
  Title,
  Subtitle,
  NotificationBox,
  NotificationHeader,
  NotificationImg,
  NotificationTitle,
  ActionsContainer,
  IgnoreButton,
  ActionButton,
} from "./styles";
// Assets
import WarningIcon from "../../../../assets/icons/ico_warning_orange.svg";
import OrangeWarningIcon from "../../../../assets/icons/ico_warning.svg";
import YellowWarningIcon from "../../../../assets/icons/ico_warning_yellow.svg";

const PendingActions = forwardRef(
  (
    {
      patientData,
      trainingHasStarted,
      clinicalScores,
      setCurrentClinicalScore,
      setShowEditClinicalScore,
      intrinsicDates,
      setNewIntrinsicDate,
      setNewMeasureKey,
      setShowIntrinsicDatePopup,
      diagnosticDates,
      setNewDiagnosticDate,
      setDiagnosticNumber,
      setDiagnosticId,
      setShowDiagnosticDatePopup,
      isLoadingPrescriptions,
      isSomePrescriptionInserted,
      setIsShowingPrescriptionsPopup,
      inactiveDays,
    },
    ref,
  ) => {
    const [clinicalScoresNotifications, setClinicalScoresNotifications] = useState([]);
    const [intrinsicDatesNotifications, setIntrinsicDatesNotifications] = useState([]);
    const [diagnosticDatesNotifications, setDiagnosticDatesNotifications] = useState([]);
    const [prescriptionsNotifications, setPrescriptionsNotifications] = useState([]);
    const [inactiveDaysNotifications, setInactiveDaysNotifications] = useState([]);
    const [removedNotifications, setRemovedNotifications] = useState({});
    const dispatch = useDispatch();
    const dismissedNotifications = useSelector(({ recsys }) => recsys.dismissedNotifications);

    // Set Clinical Scores Notifications
    useEffect(() => {
      const titleMapping = {
        pre: "Before the treatment",
        END_OF_THERAPY: "End of therapy",
        FOLLOW_UP: "Follow up",
      };
      const messageMapping = {
        pre: "Baseline clinical scores are incomplete (MoCA, ARAT, or others).",
        END_OF_THERAPY: "End of therapy clinical scores are incomplete (MoCA, ARAT, or others).",
        FOLLOW_UP: "Follow Up clinical scores are incomplete (MoCA, ARAT, or others).",
      };

      for (const clinicalScore of clinicalScores) {
        if (clinicalScore.STATUS === "MISSING_DATA") {
          const clinicalScoreKey = clinicalScore.KEY;
          setClinicalScoresNotifications((prevNotifications) => [
            ...prevNotifications,
            {
              id: `clinical_${clinicalScoreKey}`,
              title: titleMapping[clinicalScoreKey],
              message: messageMapping[clinicalScoreKey],
              buttonText: "Enter data",
              onClickEvent: () => {
                setCurrentClinicalScore(clinicalScore);
                setShowEditClinicalScore(true);
              },
              priority: clinicalScoreKey === "pre" ? "HIGH" : "MEDIUM",
              icon: OrangeWarningIcon,
              hide: false,
            },
          ]);
        }
      }

      return () => {
        setClinicalScoresNotifications([]);
      };
    }, [clinicalScores, setCurrentClinicalScore, setShowEditClinicalScore]);

    // Set Intrinsic Dates Notifications
    useEffect(() => {
      const titleMapping = {
        Baseline: "Before the treatment",
        "End of therapy": "End of therapy",
        "Follow up": "Follow up",
      };
      const notFinishedTitleMapping = {
        Baseline: "The treatment has started",
        "End of therapy": "The Follow up has started",
      };
      const notPrescribedMessageMapping = {
        Baseline: "Baseline RGS activities assessment are missing.",
        "End of therapy": "End of therapy RGS activities assessment are missing.",
        "Follow up": "Follow Up RGS activities assessment are missing.",
      };
      const notCompletedMessageMapping = {
        Baseline: "One or more Baseline RGS activities were not completed as scheduled. Set a new date for completion.",
        "End of therapy": "One or more End of therapy RGS activities were not completed as scheduled. Set a new date for completion.",
        "Follow up": "One or more Follow Up RGS activities were not completed as scheduled. Set a new date for completion.",
      };
      const notFinishedMessageMapping = {
        Baseline: "Some Baseline RGS were missed or not prescribed before RGS+ starting date, and can no longer be prescribed.",
        "End of therapy":
          "Some End of therapy RGS were missed or not prescribed before the Follow up stage, and can no longer be prescribed.",
      };

      for (const intrinsicDate of intrinsicDates) {
        const intrinsicDateKey = intrinsicDate.KEY;
        if (intrinsicDate.STATUS === "IS_NOT_PRESCRIBED") {
          setIntrinsicDatesNotifications((prevNotifications) => [
            ...prevNotifications,
            {
              id: `intrinsicDate_notPrescribed_${intrinsicDateKey}`,
              title: titleMapping[intrinsicDateKey],
              message: notPrescribedMessageMapping[intrinsicDateKey],
              buttonText: "Select date",
              onClickEvent: () => {
                setNewMeasureKey(intrinsicDateKey);
                setShowIntrinsicDatePopup(true);
              },
              priority: "HIGH",
              icon: OrangeWarningIcon,
              hide: false,
            },
          ]);
        }

        if (intrinsicDate.STATUS === "IS_NOT_COMPLETED_ORANGE") {
          setIntrinsicDatesNotifications((prevNotifications) => [
            ...prevNotifications,
            {
              id: `intrinsicDate_notCompleted_${intrinsicDateKey}`,
              title: titleMapping[intrinsicDateKey],
              message: notCompletedMessageMapping[intrinsicDateKey],
              buttonText: "Reschedule",
              onClickEvent: () => {
                setNewIntrinsicDate(new Date(moment(intrinsicDate.DATE, "DD/MM/YYYY")));
                setNewMeasureKey(intrinsicDateKey);
                setShowIntrinsicDatePopup(true);
              },
              priority: "MEDIUM",
              icon: OrangeWarningIcon,
              hide: false,
            },
          ]);
        }

        const isMissedNotification = dismissedNotifications.filter(
          (dismissedNotifications) =>
            dismissedNotifications.patientId === patientData.PATIENT_ID &&
            dismissedNotifications.notificationId === `intrinsicDate_notFinished_${intrinsicDateKey}`,
        );
        if (intrinsicDate.STATUS === "IS_NOT_COMPLETED_GRAY" && !Boolean(isMissedNotification.length)) {
          setIntrinsicDatesNotifications((prevNotifications) => [
            ...prevNotifications,
            {
              id: `intrinsicDate_notFinished_${intrinsicDateKey}`,
              title: notFinishedTitleMapping[intrinsicDateKey],
              message: notFinishedMessageMapping[intrinsicDateKey],
              priority: "LOW",
              icon: YellowWarningIcon,
              hide: true,
            },
          ]);
        }
      }

      return () => {
        setIntrinsicDatesNotifications([]);
      };
    }, [intrinsicDates, dismissedNotifications, patientData, setNewIntrinsicDate, setNewMeasureKey, setShowIntrinsicDatePopup]);

    // Set Diagnostic Dates Notifications
    useEffect(() => {
      const filteredDiagnosticDates = diagnosticDates.filter(
        (diagnosticDate) => diagnosticDate.DIAGNOSTIC_KEY === "diagnostic1" || diagnosticDate.DIAGNOSTIC_KEY === "diagnostic2",
      );

      filteredDiagnosticDates.forEach((diagnosticDate, i) => {
        if (diagnosticDate.STATUS === "NOT_COMPLETED") {
          setDiagnosticDatesNotifications((prevNotifications) => [
            ...prevNotifications,
            {
              id: `diagnosticDates_notCompleted_${diagnosticDate.DIAGNOSTIC_KEY}`,
              title: "Incomplete Diagnosis",
              message: "The diagnostic session wasn't completed as scheduled. Please set a new date for completion.",
              buttonText: "Reschedule",
              onClickEvent: () => {
                setNewDiagnosticDate(new Date(moment(diagnosticDate.DIAGNOSTIC_DATE, "DD/MM/YYYY")));
                setDiagnosticNumber(i);
                setDiagnosticId(diagnosticDate.RECSYS_DIAGNOSTIC_ID);
                setShowDiagnosticDatePopup(true);
              },
              priority: "MEDIUM",
              icon: OrangeWarningIcon,
              hide: false,
            },
          ]);
        }
      });

      const notFinishedDates = filteredDiagnosticDates.filter((diagnosticDate) => diagnosticDate.STATUS === "NOT_COMPLETED_GRAY");
      const isMissedNotification = dismissedNotifications.filter(
        (dismissedNotifications) =>
          dismissedNotifications.patientId === patientData.PATIENT_ID &&
          dismissedNotifications.notificationId === `diagnosticDates_notFinished`,
      );
      if (notFinishedDates.length && !Boolean(isMissedNotification.length)) {
        setDiagnosticDatesNotifications((prevNotifications) => [
          ...prevNotifications,
          {
            id: `diagnosticDates_notFinished`,
            title: "Incomplete Diagnosis",
            message: "The previous diagnostic assessment wasn't completed before the next one started, and can no longer be prescribed.",
            priority: "LOW",
            icon: YellowWarningIcon,
            hide: true,
          },
        ]);
      }

      return () => {
        setDiagnosticDatesNotifications([]);
      };
    }, [
      diagnosticDates,
      dismissedNotifications,
      patientData,
      setNewDiagnosticDate,
      setDiagnosticNumber,
      setDiagnosticId,
      setShowDiagnosticDatePopup,
    ]);

    // Set Prescriptions Notifications
    useEffect(() => {
      const patientGroup = patientData.aisnData.AISN_GROUP;
      const startingDate = patientData.aisnData.ROUTINE_STARTING_DATE;

      const isEnableThePrescriptionsEdition = () => {
        if (!trainingHasStarted) return true;
        return moment(startingDate, "DD/MM/YYYY").format("dddd") === moment().format("dddd");
      };

      if (!isLoadingPrescriptions && !isSomePrescriptionInserted && patientGroup !== "Control" && isEnableThePrescriptionsEdition()) {
        const handleSetRecSysPrescriptions = () => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        const handleSetNoRecSysPrescriptions = () => setIsShowingPrescriptionsPopup(true);

        setPrescriptionsNotifications([
          {
            id: `missingPrescription`,
            title: "Missing Prescription",
            message:
              patientGroup === "RGS+AI" ? "The prescription has not yet been accepted (or modified)." : "The prescription is missing.",
            buttonText: patientGroup === "RGS+AI" ? "Go to prescription" : "Add prescription",
            onClickEvent: patientGroup === "RGS+AI" ? handleSetRecSysPrescriptions : handleSetNoRecSysPrescriptions,
            priority: "HIGH",
            icon: OrangeWarningIcon,
            hide: false,
          },
        ]);
      }

      return () => {
        setPrescriptionsNotifications([]);
      };
    }, [patientData, trainingHasStarted, isLoadingPrescriptions, isSomePrescriptionInserted, setIsShowingPrescriptionsPopup, ref]);

    // Set Inactive days Notifications
    useEffect(() => {
      const inactiveDaysLimit = 2;
      if (inactiveDays >= inactiveDaysLimit) {
        setInactiveDaysNotifications([
          {
            id: `inactivePatient`,
            title: "Inactive Patient",
            message: `The patient has been inactive for ${inactiveDays} days with prescribed activities. Consider contacting the patient.`,
            priority: "LOW",
            icon: YellowWarningIcon,
            hide: false,
          },
        ]);
      }

      return () => {
        setInactiveDaysNotifications([]);
      };
    }, [inactiveDays]);

    const removeNotificationById = (id) => {
      setClinicalScoresNotifications((prev) => prev.filter((n) => n.id !== id));
      setIntrinsicDatesNotifications((prev) => prev.filter((n) => n.id !== id));
      setDiagnosticDatesNotifications((prev) => prev.filter((n) => n.id !== id));
      setPrescriptionsNotifications((prev) => prev.filter((n) => n.id !== id));
      setInactiveDaysNotifications((prev) => prev.filter((n) => n.id !== id));
    };

    const renderNotifications = () => {
      const notifications = clinicalScoresNotifications.concat(
        intrinsicDatesNotifications,
        diagnosticDatesNotifications,
        prescriptionsNotifications,
        inactiveDaysNotifications,
      );
      const sortNotifications = (priority) => notifications.filter((notification) => notification.priority === priority);

      const highNotifications = sortNotifications("HIGH");
      const mediumNotifications = sortNotifications("MEDIUM");
      const lowNotifications = sortNotifications("LOW");

      const sortedNotifications = highNotifications.concat(mediumNotifications, lowNotifications);

      if (sortedNotifications.length) {
        return (
          <MainDiv>
            <Header>
              <WarningImg src={WarningIcon} alt="warning-ico" />
              <TitlesBox>
                <Title>Pending Actions</Title>
                <Subtitle> Action is required to continue the process</Subtitle>
              </TitlesBox>
            </Header>
            {sortedNotifications.map((notification, i) => {
              const notificationId = notification.id;
              const hideAfterIgnoring = notification.hide;
              const isHighPriority = notification.priority === "HIGH";
              const isMediumPriority = notification.priority === "MEDIUM";
              const isLowPriority = notification.priority === "LOW";

              const ignoreButtonText = isMediumPriority ? "Ignore" : "Dismiss";

              const handleIgnoreNotification = () => {
                setRemovedNotifications((prev) => ({ ...prev, [notificationId]: true }));
              };

              const handleIgnoreNotificationAfterAnimation = () => {
                if (removedNotifications[notificationId]) {
                  removeNotificationById(notificationId);
                  setRemovedNotifications((prev) => {
                    const copy = { ...prev };
                    delete copy[notificationId];
                    return copy;
                  });
                  if (hideAfterIgnoring) {
                    const newDismissedNotifications = [
                      ...dismissedNotifications,
                      { patientId: patientData.PATIENT_ID, notificationId: notificationId },
                    ];
                    dispatch(addDismissedNotification({ newDismissedNotifications }));
                  }
                }
              };

              return (
                <NotificationBox
                  key={i}
                  leaving={!!removedNotifications[notificationId]}
                  onAnimationEnd={handleIgnoreNotificationAfterAnimation}
                >
                  <NotificationHeader>
                    <NotificationImg src={notification.icon} alt="warning-ico" />
                    <NotificationTitle>{notification.title}</NotificationTitle>
                  </NotificationHeader>
                  <Subtitle>{notification.message}</Subtitle>
                  <ActionsContainer justifyContent={notification.priority === "MEDIUM" ? "space-between" : "flex-end"}>
                    {!isHighPriority && (
                      <IgnoreButton
                        onClick={handleIgnoreNotification}
                        border={isMediumPriority ? "2px solid #f15a24" : "none"}
                        background={isMediumPriority ? "transparent" : "#F7931E"}
                        color={isMediumPriority ? "#f15a24" : "#fff"}
                      >
                        {ignoreButtonText}
                      </IgnoreButton>
                    )}
                    {!isLowPriority && <ActionButton onClick={notification.onClickEvent}>{notification.buttonText}</ActionButton>}
                  </ActionsContainer>
                </NotificationBox>
              );
            })}
          </MainDiv>
        );
      }

      return null;
    };

    return renderNotifications();
  },
);

export default PendingActions;
